import { useMemo, useState } from 'react';
import { OptionRow } from '../../primitives';
import {
  StepCta,
  StepHeading,
  StepStack,
  StepSub,
  TertiaryLink,
} from '../../OnboardingShell/StepLayout';
import {
  COMPETITIVE_JOURNEYS,
  QUAL_PACE_OPTIONS,
  type JourneyId,
  type QualPaceId,
} from '../../../fixtures/onboarding';
import {
  ONBOARDING_RACE_DISTANCES,
  calcPaces,
  formatRaceTime,
  parseRaceTime,
  type RaceDistance,
} from '../../../lib/vdot';
import type { OnboardingAnswers } from '../../../hooks/onboardingReducer';
import { raceTimeUsable } from '../../../hooks/usePersistOnboarding';
import styles from './Step5Pace.module.css';

/**
 * B440 (rulings 2026-10-21 item 12) — the race time box's two sentences, one
 * plain line each. FORMAT names what the box reads; FIT says a well-formed time
 * cannot set paces for this distance (the completion write would have dropped
 * it without a word, so it is named instead).
 */
const RACE_TIME_FORMAT_SENTENCE =
  'Use minutes and seconds, like 22:30, hours too, like 3:45:00, or type digits only, like 2230.';
const RACE_TIME_FIT_SENTENCE =
  'That time does not fit this distance, so check the time or the distance.';
/** B465 — a time typed with no distance chosen names the missing distance. */
const RACE_DISTANCE_MISSING_SENTENCE = 'Choose the race distance for this time.';
/** An hour-form example for the long races; the box takes it on any distance. */
const TIME_PLACEHOLDER: Partial<Record<RaceDistance, string>> = {
  'Half Marathon': '1:45:00',
  Marathon: '3:45:00',
};
/** UX M2 — on a Half Marathon or a Marathon the usual way to land here is a
 *  typist who meant hours ("345" reads 3:45 MINUTES), so the sentence says so. */
function raceTimeFitSentence(distance: RaceDistance): string {
  const example = TIME_PLACEHOLDER[distance];
  return example
    ? `That time does not fit this distance, so check the time or the distance, and include the hours, like ${example}.`
    : RACE_TIME_FIT_SENTENCE;
}

type Step5Props = {
  answers: OnboardingAnswers;
  setAnswer: (patch: Partial<OnboardingAnswers>) => void;
  onContinue: () => void;
};

// Phase 6b1.5 PR 1 — switched from `Object.keys(BASE_REFERENCE_MINUTES)`
// (the old approximation table's keys) to the explicit
// `ONBOARDING_RACE_DISTANCES` export now that the underlying
// algorithm is Daniels & Gilbert. Same 7 distance labels, same
// `<select>` enumeration order.
const RACE_DISTANCES = ONBOARDING_RACE_DISTANCES;

/**
 * Step 5 — Pace (BRANCHED by journey).
 *
 * - Competitive (competitive_solo / competitive_team) + time_goal:
 *   VDOT race-time calculator. Distance dropdown + MM:SS time input.
 *   Live 4-zone preview (Easy / Tempo / Interval / Long) animates in
 *   via fadeUp when both inputs are filled.
 *
 * - Health (health_fitness) + social: qualitative 4-row selector
 *   mapping to VDOT bands (28-32 / 32-38 / 38-42 / 42-48).
 *
 * Both branches include a skip path: "My coaches can estimate from
 * my first few runs" — sets `skipPace: true` and advances. The
 * onboarding chat agent picks up the slack later.
 *
 * Ruling 3 (2026-10-22) — the O2 universal pace screen ("Do you know your
 * usual pace?", exported from here as `Step5PaceUniversal`) is gone: it
 * collected a pace and nothing ever used it. The health/social walk asks this
 * level question and moves on; the competitive/time-goal walk answers its pace
 * question through the VDOT form below, which is untouched.
 *
 * ⛔ `Step5PaceVdot` IS PINNED BYTE-IDENTICAL (`Step5Pace.test.tsx`,
 * "the VDOT form is byte-identical"). It is the calculator the whole
 * competitive plan is derived from, it was not in the handoff's scope, and a
 * silent edit here would move every zone in every competitive plan. Anything
 * that reads as an improvement to it belongs in its own lane with its own
 * ratification.
 */
export function Step5Pace({ answers, setAnswer, onContinue }: Step5Props) {
  const journey: JourneyId = answers.journey ?? 'health_fitness';
  const isVdotBranch = COMPETITIVE_JOURNEYS.includes(journey);

  if (isVdotBranch) {
    return (
      <Step5PaceVdot
        answers={answers}
        setAnswer={setAnswer}
        onContinue={onContinue}
      />
    );
  }
  return (
    <Step5PaceQualitative
      answers={answers}
      setAnswer={setAnswer}
      onContinue={onContinue}
    />
  );
}

// ──────────────────────────────────────────────────────────────────────────
// VDOT branch — competitive + time_goal
// ──────────────────────────────────────────────────────────────────────────

function Step5PaceVdot({ answers, setAnswer, onContinue }: Step5Props) {
  // Time input is held as a string locally so the user can type
  // freely (e.g. "5:" mid-typing); we only commit to state when the
  // string parses cleanly.
  const [timeInput, setTimeInput] = useState<string>(
    answers.raceTimeSeconds ? formatRaceTime(answers.raceTimeSeconds) : '',
  );
  // B440 — a problem is named once the athlete has left the box (or changed
  // the distance under a time), never while a valid time is still being typed.
  // A time resumed from an earlier visit counts as already entered.
  const [timeTouched, setTimeTouched] = useState<boolean>(!!answers.raceTimeSeconds);

  const distance = (answers.raceDistance as RaceDistance | undefined) ?? null;
  const seconds = answers.raceTimeSeconds ?? null;
  const usable = raceTimeUsable(distance ?? undefined, seconds ?? undefined);
  const paces = useMemo(
    () => (usable ? calcPaces(distance, seconds) : null),
    [usable, distance, seconds],
  );
  const timeProblem =
    timeInput.trim() !== '' && seconds == null
      ? RACE_TIME_FORMAT_SENTENCE
      : seconds != null && distance && !usable
        ? raceTimeFitSentence(distance)
        : null;

  const onTimeChange = (raw: string) => {
    setTimeInput(raw);
    // B440 — the stored time is ALWAYS the one the box shows: text that does
    // not parse clears it (it used to keep the last valid time, so "22:30"
    // edited to "abc" still saved 22:30).
    const parsed = parseRaceTime(raw);
    setAnswer({ raceTimeSeconds: parsed ?? undefined });
  };

  const onDistanceChange = (d: RaceDistance) => {
    setAnswer({ raceDistance: d });
    if (timeInput.trim() !== '') setTimeTouched(true);
  };

  const ready = !!distance && seconds != null && seconds > 0 && usable;
  // B465 — a time (even text that is not a time) with no distance: Continue stays
  // pressable and a press, or leaving the box, names the missing distance. It used
  // to look active and do nothing, with no sentence anywhere.
  const distanceNeeded = !distance && timeInput.trim() !== '';
  const distanceProblem = distanceNeeded && timeTouched ? RACE_DISTANCE_MISSING_SENTENCE : null;
  const shownProblem = timeTouched && timeProblem ? timeProblem : distanceProblem;

  // UX M3 — pressing Continue on a value that is wrong names the problem (the
  // Step 4 pattern: pressable, `aria-disabled`, "touched" on press) and does not
  // advance. With nothing wrong to name (no time yet, or no distance yet) the
  // button stays natively disabled, as before.
  const handleContinue = () => {
    if (!ready) {
      setTimeTouched(true);
      return;
    }
    onContinue();
  };

  const handleSkip = () => {
    setAnswer({ skipPace: true });
    onContinue();
  };

  return (
    <>
      <StepHeading>What&rsquo;s your recent race time?</StepHeading>
      <StepSub>
        Your coaches use this to set zone-2 easy pace, tempo, intervals,
        and long-run pace. Pick any recent race or time-trial.
      </StepSub>

      <div className={styles.vdotInputs}>
        <div className={styles.vdotField}>
          <label className={styles.vdotLabel} htmlFor="ob-distance">
            Distance
          </label>
          <select
            id="ob-distance"
            className={styles.vdotSelect}
            value={distance ?? ''}
            onChange={(e) => onDistanceChange(e.target.value as RaceDistance)}
          >
            <option value="" disabled>
              Pick…
            </option>
            {RACE_DISTANCES.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.vdotField}>
          <label className={styles.vdotLabel} htmlFor="ob-time">
            Time
          </label>
          <input
            id="ob-time"
            className={styles.vdotInput}
            type="text"
            inputMode="numeric"
            // B473 — the phone's number keypad has no colon: the example is the
            // digits form ("2230"), and the reading line below says how it reads.
            placeholder={((distance && TIME_PLACEHOLDER[distance]) || '22:30').replace(/:/g, '')}
            value={timeInput}
            onChange={(e) => onTimeChange(e.target.value)}
            onBlur={() => setTimeTouched(true)}
            aria-invalid={timeTouched && timeProblem ? true : undefined}
            aria-describedby={shownProblem ? 'ob-time-problem' : undefined}
          />
        </div>
      </div>
      {/* B460 — the sentence's room is reserved, so it appearing never moves the
          preview or the buttons under it. */}
      <div className={styles.problemSlot}>
        {shownProblem && (
          <p id="ob-time-problem" role="alert" className={styles.fieldProblem}>
            {shownProblem}
          </p>
        )}
        {/* B473 — the Settings Recalculate pattern (B50): say how the digits read. */}
        {/* Only a time the step will take: "345" on a Marathon must not read back as
            3:45 before the blur refuses it (review 2026-10-22). */}
        {!shownProblem && distance && raceTimeUsable(distance, parseRaceTime(timeInput) ?? undefined) && (
          <p className={styles.timeReading} aria-live="polite">
            Reads as {formatRaceTime(parseRaceTime(timeInput) as number)}
          </p>
        )}
      </div>

      {paces && (
        <div className={styles.preview} aria-live="polite">
          {paces.map((zone) => (
            <div key={zone.id}>
              <div className={styles.previewLabel}>{zone.label}</div>
              <div className={styles.previewPace}>{zone.pace}</div>
            </div>
          ))}
        </div>
      )}

      <StepCta
        onClick={handleContinue}
        disabled={!ready && !timeProblem && !distanceNeeded}
        aria-disabled={!ready && (timeProblem || distanceNeeded) ? true : undefined}
      >
        Continue
      </StepCta>
      <TertiaryLink onClick={handleSkip}>
        No recent race? My coaches can estimate from my first few runs.
      </TertiaryLink>
    </>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Qualitative branch — health_fitness + social
// ──────────────────────────────────────────────────────────────────────────

function Step5PaceQualitative({ answers, setAnswer, onContinue }: Step5Props) {
  const ready = !!answers.qualPace;

  const handleSkip = () => {
    setAnswer({ skipPace: true });
    onContinue();
  };

  return (
    <>
      <StepHeading>Where are you right now?</StepHeading>
      <StepSub>
        Honest answer: your coaches start from here, not from where you
        think you should be.
      </StepSub>

      <StepStack>
        {QUAL_PACE_OPTIONS.map((opt) => (
          <OptionRow
            key={opt.id}
            label={opt.label}
            sub={opt.description}
            selected={answers.qualPace === opt.id}
            onClick={() => setAnswer({ qualPace: opt.id as QualPaceId })}
          />
        ))}
      </StepStack>

      <StepCta onClick={onContinue} disabled={!ready}>
        Continue
      </StepCta>
      <TertiaryLink onClick={handleSkip}>
        My coaches can estimate from my first few runs.
      </TertiaryLink>
    </>
  );
}
